import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  TableCell,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Pagination,
  TextField,
  Box,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ListItemText,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";
import {
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonHeader,
  IonPage,
  IonTitle,
  IonContent,
} from "@ionic/react";

type PurchaseOrder = {
  id: string;
  product_name: string;
  product_id: string;
  request_id: string;
  quantity: number | string;
  po_qty: number | string;
  status: string;
  unit_price: string;
  total_price: string;
};

type Officer = {
  id: string;
  name: string;
  email: string;
  role: string;
};

const statusTabs = [
  { value: "approval", label: "Approval" },
  { value: "auditing", label: "Auditing" },
  { value: "confirmed", label: "Confirmed" },
];

const PurchaseOrderPresidentApprovalSales: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [approvedQty, setApprovedQty] = useState("");
  const [currentTab, setCurrentTab] = useState("approval");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [officers, setOfficers] = useState<Officer[]>([]);
  const [selectedOfficer, setSelectedOfficer] = useState<string>("");
  const [selectedRequestForAssignment, setSelectedRequestForAssignment] =
    useState<PurchaseOrder | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    id: string;
    action: "accept" | "rejected";
  }>({ open: false, id: "", action: "accept" });

  const [assignDialog, setAssignDialog] = useState({
    open: false,
    requestId: "",
    productId: "",
  });

  useEffect(() => {
    fetchOrders();
    fetchOfficers();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, currentTab, searchTerm]);

  const fetchOrders = () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("requestID", requestID());

    axios
      .post(
        baseUrl() + "warehouses/getVettingPurchaseRequests2",
        formData,
        config
      )
      .then((res) => {
       // console.log(res.data.data);
                console.log(res);

        const processedOrders = (res.data.data || []).map((order: any) => ({
          ...order,
          quantity: Number(order.quantity),
          po_qty: Number(order.po_qty),
          vetted_qty:
            Number(order.vetted_qty),
          approved_qty:
            Number(order.approved_qty) > 0
              ? Number(order.approved_qty)
              : Number(order.vetted_qty),
              approvedQty:setApprovedQty(order.vetted_qty),
          total_price: (
            Number(order.quantity) * Number(order.unit_price)
          ).toFixed(2),
        }));
        setOrders(processedOrders);
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch purchase orders");
      })
      .finally(() => setIsLoading(false));
  };

  const fetchOfficers = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());

    axios
      .post(baseUrl() + "users/getPurchasingOfficers", formData, config)
      .then((res) => {
        console.log(res);
        setOfficers(res.data.data || []);
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch officers");
      });
  };

  const filterOrders = () => {
    let filtered = orders.filter(
      (order) => order.status.toLowerCase() === currentTab.toLowerCase()
    );

    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.request_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    fetchOrders()
    setCurrentTab(newValue);
  };

  const handleQuantityChange = (id: string, value: string) => {
    const updatedOrders = orders.map((order) =>
      order.id === id ? { ...order, approved_qty: Number(value) } : order
    );
    setApprovedQty(value);
    setOrders(updatedOrders);
  };

  const confirmStatusChange = (id: string, action: "accept" | "rejected") => {
    setConfirmDialog({ open: true, id, action });
  };

  const handleStatusChange = () => {
    const { id, action } = confirmDialog;
    const order = orders.find((o) => o.id === id);
    if (!order) return;

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("request_id", order.request_id);
    formData.append("product_id", order.product_id);
    formData.append("status", action);
    formData.append("quantity", String(approvedQty));

    axios
      .post(
        baseUrl() + "warehouses/presidentOfficerApprovePurchaseRequest",
        formData,
        config
      )
      .then((res) => {
        console.log(res);
        alert(`Order ${action}`);
        fetchOrders();
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to update status");
      })
      .finally(() =>
        setConfirmDialog({ open: false, id: "", action: "accept" })
      );
  };

  const handleAssignClick = (
    event: React.MouseEvent<HTMLElement>,
    request: PurchaseOrder
  ) => {
    setSelectedRequestForAssignment(request);
    setAnchorEl(event.currentTarget);
  };

  const handleAssignToOfficer = () => {
    if (!selectedRequestForAssignment || !selectedOfficer) return;

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("request_id", selectedRequestForAssignment.request_id);
    formData.append("product_id", selectedRequestForAssignment.product_id);
    formData.append("officer_id", selectedOfficer);

    axios
      .post(
        baseUrl() + "warehouses/assignOfficerToPurchaseRequest",
        formData,
        config
      )
      .then((res) => {
        console.log(res);
        alert("Request assigned successfully");
        fetchOrders();
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to assign request");
      })
      .finally(() => {
        setAssignDialog({ open: false, requestId: "", productId: "" });
        setSelectedOfficer("");
        setSelectedRequestForAssignment(null);
      });
  };

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredOrders.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  return (
    <IonPage className="puahDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Purchase Order President Approval</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box sx={{ p: 3 }}>
          {/* Search and Filter Section */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search Products or Request ID"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 300 }}
            />

            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {statusTabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
                Loading purchase orders...
              </Typography>
            </Box>
          ) : currentRows.length > 0 ? (
            <>
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>
                        <strong>Product</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Request ID</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Requested Qty</strong>
                      </TableCell>
                      {/* <TableCell>
                        <strong>P.Order Qty</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Vetting Order Qty</strong>
                      </TableCell>*/}
                      <TableCell> 
                        <strong>Approved Order Qty</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRows.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.product_name}</TableCell>
                        <TableCell>{order.request_id}</TableCell>
                        <TableCell>{order.quantity}</TableCell>
                        {/* <TableCell>{order?.po_qty}</TableCell>
                        <TableCell>{order?.vetted_qty}</TableCell> */}
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            disabled={order.status != "approval"}
                            value={order.approved_qty}
                            onChange={(e) =>
                              handleQuantityChange(order.id, e.target.value)
                            }
                            inputProps={{ min: 0, max: order.quantity }}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor:
                                order.status === "accept"
                                  ? "success.light"
                                  : order.status === "rejected"
                                  ? "error.light"
                                  : "grey.200",
                              color:
                                order.status === "accept"
                                  ? "success.dark"
                                  : order.status === "rejected"
                                  ? "error.dark"
                                  : "grey.800",
                              fontWeight: "medium",
                            }}
                          >
                          Awaiting  {order.status.charAt(0).toUpperCase() +
                              order.status.slice(1)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {order.status === "approval" && (
                            <Grid container spacing={1}>
                              <Grid item>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() =>
                                    confirmStatusChange(order.id, "accept")
                                  }
                                  // disabled={order.status !== "vetting"}
                                >
                                  Approve
                                </Button>
                              </Grid>
                              <Grid item>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    confirmStatusChange(order.id, "rejected")
                                  }
                                  disabled={order.status === "rejected"}
                                >
                                  Reject
                                </Button>
                              </Grid>
                            </Grid>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          ) : (
            <Typography variant="body1" sx={{ mt: 4, textAlign: "center" }}>
              No {currentTab} purchase orders found.
            </Typography>
          )}

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialog.open}
            onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
          >
            <DialogTitle>
              Confirm{" "}
              {confirmDialog.action === "accept" ? "Approval" : "Rejection"}
            </DialogTitle>
            <DialogContent>
              Are you sure you want to {confirmDialog.action} this order?
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setConfirmDialog({ ...confirmDialog, open: false })
                }
              >
                Cancel
              </Button>
              <Button onClick={handleStatusChange} color="primary" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Assign to Officer Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAssignDialog({
                  open: true,
                  requestId: selectedRequestForAssignment?.request_id || "",
                  productId: selectedRequestForAssignment?.product_id || "",
                });
                setAnchorEl(null);
              }}
            >
              Assign to Officer
            </MenuItem>
          </Menu>

          {/* Assign to Officer Dialog */}
          <Dialog
            open={assignDialog.open}
            onClose={() => setAssignDialog({ ...assignDialog, open: false })}
          >
            <DialogTitle>Assign Request to Officer</DialogTitle>
            <DialogContent sx={{ minWidth: 400, pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Officer</InputLabel>
                <Select
                  value={selectedOfficer}
                  onChange={(e) => setSelectedOfficer(e.target.value)}
                  label="Select Officer"
                  variant="outlined"
                  renderValue={(selected) => {
                    const officer = officers.find(
                      (o) => o.user_id === selected
                    );
                    return officer ? officer.firstName : "";
                  }}
                >
                  {officers.map((officer, i) => (
                    <MenuItem key={i} value={officer.user_id}>
                      <Checkbox checked={selectedOfficer === officer.user_id} />
                      <ListItemText
                        primary={officer.firstName + " " + officer.lastName}
                        secondary={officer.email}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setAssignDialog({ ...assignDialog, open: false })
                }
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignToOfficer}
                color="primary"
                autoFocus
                disabled={!selectedOfficer}
              >
                Assign
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default PurchaseOrderPresidentApprovalSales;
